import { soApi } from "@src/api/so";
import { lotsServices, type ApiStockLot } from "../../../../../api/lots";
import type { StockGatheringReserveItem } from "../../../../../api/stockGatherings";

/** Результат поиска резерва КВ по скану партии. */
export type StockGatheringCompletionReserveScanResult =
    | { type: "notFound" }
    | { type: "reserveNotFound"; lot: ApiStockLot }
    | { type: "ambiguous"; lot: ApiStockLot; reserves: StockGatheringReserveItem[] }
    | { type: "single"; reserve: StockGatheringReserveItem };

/**
 * Ищет партию по скану и сопоставляет её с резервами текущей КВ.
 * @param barcode отсканированная этикетка партии
 * @param reserves резервы текущей КВ
 */
export async function resolveStockGatheringCompletionReserveScan(
    barcode: string,
    reserves: StockGatheringReserveItem[],
): Promise<StockGatheringCompletionReserveScanResult> {
    const scannedObject = await soApi.findObject({ soNum: barcode });
    const lotsByCode = isValidCode(scannedObject.soCode)
        ? await lotsServices.load({ flt: { Codes: [scannedObject.soCode] } })
        : [];
    const lotsByText = await lotsServices.load({ flt: { SoNum: barcode } });
    const lots = uniqueLots([...lotsByCode, ...lotsByText]);

    if (lots.length === 0) {
        return { type: "notFound" };
    }

    const matchedReserves = lots.flatMap((lot) =>
        reserves.filter((reserve) => reserve.LotCode === lot.LotCode).map((reserve) => ({ lot, reserve })),
    );

    if (matchedReserves.length === 0) {
        return { type: "reserveNotFound", lot: lots[0] };
    }

    if (matchedReserves.length > 1) {
        return {
            type: "ambiguous",
            lot: matchedReserves[0].lot,
            reserves: matchedReserves.map((item) => item.reserve),
        };
    }

    return { type: "single", reserve: matchedReserves[0].reserve };
}

function uniqueLots(lots: ApiStockLot[]): ApiStockLot[] {
    const result: ApiStockLot[] = [];
    const seen = new Set<number>();

    lots.forEach((lot) => {
        if (!seen.has(lot.LotCode)) {
            seen.add(lot.LotCode);
            result.push(lot);
        }
    });

    return result;
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
